/**
 * Credit card validation utilities
 */

/**
 * Validates credit card number
 * @param {string} cardNumber - The card number to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validateCardNumber = (cardNumber) => {
  if (!cardNumber) {
    return { isValid: false, error: 'Card number is required' };
  }

  // Remove spaces and non-digits
  const cleanNumber = cardNumber.replace(/\D/g, '');

  // Check length (maximum 19 digits)
  if (cleanNumber.length > 19) {
    return { isValid: false, error: 'Card number must be less than 19 digits' };
  }

  // Luhn algorithm validation
  // if (!isValidLuhn(cleanNumber)) {
  //   return { isValid: false, error: 'Invalid card number' };
  // }

  return { isValid: true };
};

/**
 * Validates cardholder name
 * @param {string} cardHolder - The cardholder name to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validateCardHolder = (cardHolder) => {
  if (!cardHolder) {
    return { isValid: false, error: 'Cardholder name is required' };
  }

  // Check length (less than 30 English characters)
  if (cardHolder.length >= 30) {
    return { isValid: false, error: 'Cardholder name must be less than 30 characters' };
  }

  // Check for English characters only (letters, spaces, hyphens, apostrophes)
  const englishOnlyRegex = /^[A-Za-z\s\-']+$/;
  if (!englishOnlyRegex.test(cardHolder)) {
    return { isValid: false, error: 'Cardholder name must contain only English characters' };
  }

  return { isValid: true };
};

/**
 * Validates expiry date in MM/YY format
 * @param {string} expiry - The expiry date to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validateExpiryDate = (expiry) => {
  if (!expiry) {
    return { isValid: false, error: 'Expiry date is required' };
  }

  // Check format MM/YY
  const expiryRegex = /^(0[1-9]|1[0-2])\/\d{2}$/;
  if (!expiryRegex.test(expiry)) {
    return { isValid: false, error: 'Expiry date must be in MM/YY format' };
  }

  // Check if card is expired
  const [month, year] = expiry.split('/');
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear() % 100; // Get last 2 digits
  const currentMonth = currentDate.getMonth() + 1; // 0-based to 1-based

  const cardYear = parseInt(year, 10);
  const cardMonth = parseInt(month, 10);

  if (cardYear < currentYear || (cardYear === currentYear && cardMonth < currentMonth)) {
    return { isValid: false, error: 'Card has expired' };
  }

  return { isValid: true };
};

/**
 * Validates CVV/CVC code
 * @param {string} cvv - The CVV code to validate
 * @returns {{ isValid: boolean, error?: string }}
 */
export const validateCVV = (cvv) => {
  if (!cvv) {
    return { isValid: false, error: 'CVV is required' };
  }

  // Remove any non-digits
  const cleanCVV = cvv.replace(/\D/g, '');

  // Check length (3-4 digits)
  if (cleanCVV.length < 3 || cleanCVV.length > 4) {
    return { isValid: false, error: 'CVV must be 3-4 digits' };
  }

  // Check if it's all digits
  if (!/^\d+$/.test(cleanCVV)) {
    return { isValid: false, error: 'CVV must contain only digits' };
  }

  return { isValid: true };
};

/**
 * Luhn algorithm implementation for credit card validation
 * @param {string} cardNumber - Clean card number (digits only)
 * @returns {boolean} - True if valid according to Luhn algorithm
 */
const isValidLuhn = (cardNumber) => {
  let sum = 0;
  let isEven = false;

  // Loop through values starting from the right
  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cardNumber.charAt(i), 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * Validates all credit card form fields
 * @param {Object} formData - The form data to validate
 * @returns {{ isValid: boolean, errors: Object.<string, string> }}
 */
export const validateCreditCardForm = (formData) => {
  const errors = {};
  let isValid = true;

  // Validate card number
  const cardNumberResult = validateCardNumber(formData.cardNumber);
  if (!cardNumberResult.isValid) {
    errors.cardNumber = cardNumberResult.error;
    isValid = false;
  }

  // Validate cardholder name
  const cardHolderResult = validateCardHolder(formData.cardHolder);
  if (!cardHolderResult.isValid) {
    errors.cardHolder = cardHolderResult.error;
    isValid = false;
  }

  // Validate expiry date
  const expiryResult = validateExpiryDate(formData.expiry);
  if (!expiryResult.isValid) {
    errors.expiry = expiryResult.error;
    isValid = false;
  }

  // Validate CVV
  const cvvResult = validateCVV(formData.cvc);
  if (!cvvResult.isValid) {
    errors.cvc = cvvResult.error;
    isValid = false;
  }

  return { isValid, errors };
};
